import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * Distância de rolagem, em pixels, a partir da qual o cabeçalho se recolhe.
 *
 * "Scrollar um pouco" — o bastante para não recolher em roçada de trackpad, e
 * bem antes de uma tela cheia, para o ganho de espaço chegar cedo.
 */
export const RECOLHER_A_PARTIR = 64;

/**
 * Distância abaixo da qual o cabeçalho volta.
 *
 * Menor que `RECOLHER_A_PARTIR` de propósito: a folga entre os dois é
 * histerese. Com um limiar só, parar a rolagem exatamente em cima dele faria o
 * cabeçalho piscar a cada pixel de movimento.
 */
export const VOLTAR_ATE = 8;

/**
 * Cabeçalho que se recolhe ao rolar o painel central e volta ao chegar no topo.
 *
 * **Escuta o elemento, não a janela** — o shell é `h-screen overflow-hidden` e
 * quem rola é o `<main>`, então `window.scrollY` é sempre 0 (mesma razão do
 * `BackToTop`, que por isso também recebe a ref).
 *
 * Recolher **devolve o espaço ao painel**, não apenas tira o cabeçalho de
 * vista: ele continua no fluxo e é puxado para cima pela própria altura, então
 * o `<main>` cresce na mesma medida. É o que a mudança existe para dar.
 *
 * Isso cria um laço em potencial: o painel cresce, a rolagem encurta, o
 * `scrollTop` pode ser truncado para baixo do limiar, o cabeçalho volta, o
 * painel encolhe — e recomeça. O guard `sobraDepois` corta o laço: só recolhe
 * se, já recolhido, ainda restar rolagem suficiente para o estado se sustentar.
 */
export function useCabecalhoRetratil<T extends HTMLElement>(
  scrollRef: RefObject<HTMLElement | null>,
  /** Espaço que o recolhimento devolve além da altura medida (o `gap` do shell). */
  folga = 0,
) {
  const cabecalhoRef = useRef<T>(null);
  const [altura, setAltura] = useState(0);
  const [recolhido, setRecolhido] = useState(false);

  // Mede antes da pintura, e de novo quando o cabeçalho muda de altura — ele
  // reflui entre mobile e desktop.
  useLayoutEffect(() => {
    const el = cabecalhoRef.current;
    if (!el) return;

    const medir = () => setAltura(el.offsetHeight);
    medir();

    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(medir);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const espacoRecolhido = altura + folga;

  useEffect(() => {
    const alvo = scrollRef.current;
    if (!alvo) return;

    const aoRolar = () => {
      // Passar o mesmo booleano não re-renderiza, então o listener pode rodar a
      // cada evento sem throttle próprio.
      setRecolhido((estava) => {
        const y = alvo.scrollTop;
        if (estava) return y > VOLTAR_ATE;
        if (y <= RECOLHER_A_PARTIR) return false;

        // `scrollHeight` e `clientHeight` forçam o navegador a calcular o
        // layout pendente antes de responder. Ficam depois do teste de limiar
        // de propósito: assim são lidos no evento em que o cabeçalho pode
        // recolher, e não nas centenas de eventos de rolagem em que não pode.
        const sobraDepois = alvo.scrollHeight - alvo.clientHeight - espacoRecolhido;
        return sobraDepois > VOLTAR_ATE;
      });
    };

    aoRolar();
    alvo.addEventListener('scroll', aoRolar, { passive: true });
    return () => alvo.removeEventListener('scroll', aoRolar);
  }, [scrollRef, espacoRecolhido]);

  return {
    cabecalhoRef,
    recolhido,
    /** Margem negativa que retira o cabeçalho do fluxo, em pixels. */
    deslocamento: recolhido ? -espacoRecolhido : 0,
    /** Traz o cabeçalho de volta — usado quando algo dentro dele recebe foco. */
    revelar: () => setRecolhido(false),
  };
}
